import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import "./CoverageDetail.css";

const DATA = {
  cancellation: {
    title: "Trip Cancellation / Interruption",
    limit: "$2,000–$5,000",
    subtitle: "Covers non-refundable expenses if your trip is canceled or cut short due to unforeseen events.",
    highlights: [
      { value: "$5K", label: "Per trip" },
      { value: "100%", label: "Non-refundable" },
      { value: "90", label: "Days / trip" },
    ],
    sections: [
      {
        heading: "What's Covered",
        items: [
          "Non-refundable flight costs",
          "Pre-paid hotel reservations",
          "Tour and activity deposits",
          "Cruise bookings",
          "Transportation expenses",
        ],
      },
      {
        heading: "Qualifying Reasons",
        items: [
          "Illness or injury (you or family member)",
          "Death of a close relative",
          "Severe weather or natural disaster",
          "Jury duty or court subpoena",
          "Unforeseen work obligations",
        ],
      },
    ],
    conditions: [
      "Must pay for the trip with your Visa card",
      "Only non-refundable portions are covered",
      "Documentation required (medical certificate, receipts)",
      "File claim within 60 days of cancellation",
    ],
    exclusions: [
      "Voluntary cancellations (change of mind)",
      "Pre-existing medical conditions",
      "Known or foreseeable events at time of booking",
      "Government-imposed travel bans",
    ],
  },
  delay: {
    title: "Travel Delay",
    limit: "$200–$500",
    subtitle: "Reimbursement for essential expenses when your trip is delayed by 6–12+ hours.",
    highlights: [
      { value: "$500", label: "Max limit" },
      { value: "6h+", label: "Min delay" },
      { value: "Per trip", label: "Coverage" },
    ],
    sections: [
      {
        heading: "What's Covered",
        items: [
          "Meals and refreshments",
          "Hotel or accommodation",
          "Essential toiletries and clothing",
          "Local transportation",
          "Communication expenses (calls, Wi-Fi)",
        ],
      },
      {
        heading: "Common Delay Causes",
        items: [
          "Airline mechanical issues",
          "Severe weather conditions",
          "Air traffic control delays",
          "Strike action",
          "Missed connections (airline's fault)",
        ],
      },
    ],
    conditions: [
      "Delay must be 6 hours or more (varies by issuer)",
      "Must have paid with your Visa card",
      "Keep all receipts for reimbursement",
      "Only reasonable and necessary expenses covered",
    ],
    exclusions: [
      "Delays you caused (late arrival at gate)",
      "Known delays at time of booking",
      "Upgrades or luxury purchases",
      "Alcohol purchases",
    ],
  },
  baggage: {
    title: "Baggage Delay / Loss",
    limit: "$100–$3,000",
    subtitle: "Compensation for delayed, lost, or stolen baggage during your travels.",
    highlights: [
      { value: "$3K", label: "Lost bag" },
      { value: "$300", label: "Delayed bag" },
      { value: "6h+", label: "Min delay" },
    ],
    sections: [
      {
        heading: "Baggage Delay",
        items: [
          "Essential clothing purchases",
          "Toiletries and personal care items",
          "Medication and health essentials",
          "Reimbursement for delay of 6+ hours",
        ],
      },
      {
        heading: "Baggage Loss / Theft",
        items: [
          "Replacement value of lost items",
          "Personal belongings compensation",
          "Luggage replacement",
          "Coverage for checked and carry-on bags",
        ],
      },
    ],
    conditions: [
      "Report loss or theft to airline immediately",
      "File a police report for stolen baggage",
      "Keep purchase receipts for claimed items",
      "Claim within 30 days of incident",
    ],
    exclusions: [
      "Unattended baggage",
      "Valuables left in checked luggage (jewelry, electronics)",
      "Items not declared at check-in",
      "Wear and tear or pre-existing damage",
    ],
  },
  accident: {
    title: "Travel Accident",
    limit: "$250K–$500K",
    subtitle: "Financial protection for serious injury or accidental death that occurs during covered travel.",
    highlights: [
      { value: "$500K", label: "Max benefit" },
      { value: "24/7", label: "Coverage" },
      { value: "Global", label: "Worldwide" },
    ],
    sections: [
      {
        heading: "What's Covered",
        items: [
          "Accidental death benefit",
          "Loss of limb(s)",
          "Loss of sight",
          "Loss of speech or hearing",
          "Permanent total disability",
        ],
      },
      {
        heading: "Benefit Schedule",
        items: [
          "Full amount — accidental death or loss of two limbs/eyes",
          "50% — loss of one limb or sight in one eye",
          "25% — loss of thumb and index finger",
          "Benefit paid to insured or beneficiary",
        ],
      },
    ],
    conditions: [
      "Accident must occur during a covered trip",
      "Must have paid for travel with Visa card",
      "Death or injury within 365 days of accident",
      "Medical documentation required",
    ],
    exclusions: [
      "Self-inflicted injuries",
      "Injuries from high-risk / extreme sports",
      "Alcohol or drug-related incidents",
      "Acts of war or terrorism (some exceptions)",
      "Pre-existing medical conditions",
    ],
  },
  rental: {
    title: "Rental Car Insurance",
    limit: "Secondary Coverage",
    subtitle: "Covers damage or theft of your rental vehicle when you pay with your Visa card.",
    highlights: [
      { value: "CDW", label: "Damage waiver" },
      { value: "Theft", label: "Protection" },
      { value: "31 days", label: "Max rental" },
    ],
    sections: [
      {
        heading: "What's Covered",
        items: [
          "Collision damage to rental vehicle",
          "Theft of rental vehicle",
          "Vandalism damage",
          "Windshield and glass damage",
          "Valid towing charges",
        ],
      },
      {
        heading: "How It Works",
        items: [
          "Decline the rental company's CDW/LDW at the counter",
          "Pay the full rental cost with your Visa card",
          "Coverage kicks in as secondary to your personal auto insurance",
          "Report any incident within 48 hours",
        ],
      },
    ],
    conditions: [
      "Pay the full rental with your Visa card",
      "Rental period must be 31 days or less",
      "Must decline rental company's CDW/LDW",
      "Driver must be listed on the rental agreement",
      "Valid driver's license required",
    ],
    exclusions: [
      "Trucks, vans, or exotic/luxury vehicles",
      "Off-road driving damage",
      "Interior damage or personal belongings inside",
      "Driving under the influence",
      "Tire or mechanical damage (non-collision)",
    ],
  },
  assistance: {
    title: "24/7 Travel Assistance",
    limit: "Unlimited",
    subtitle: "Round-the-clock access to emergency support services anywhere in the world.",
    highlights: [
      { value: "24/7", label: "Available" },
      { value: "Global", label: "Coverage" },
      { value: "Free", label: "To call" },
    ],
    sections: [
      {
        heading: "Medical Assistance",
        items: [
          "Doctor and hospital referrals",
          "Coordination of emergency medical transport",
          "Prescription assistance and replacement",
          "Medical monitoring during hospitalization",
          "Guarantee of hospital payment",
        ],
      },
      {
        heading: "Travel Assistance",
        items: [
          "Emergency travel arrangements",
          "Lost passport and document assistance",
          "Legal referrals and bail bond assistance",
          "Translation and interpreter services",
          "Emergency message relay to family",
        ],
      },
    ],
    conditions: [
      "Must be traveling away from home",
      "Trip paid with your Visa card",
      "Services are referral and coordination based",
      "Actual costs of services may apply separately",
    ],
    exclusions: [
      "Non-emergency or routine inquiries",
      "Services in your home city",
      "Cost of actual medical treatment (covered separately)",
      "Pre-arranged appointments",
    ],
  },
};

export default function CoverageDetail() {
  const { id } = useParams();
  const coverage = DATA[id];

  if (!coverage) return <Navigate to="/travel-insurance" replace />;

  return (
    <div className="cov-detail-page">
      <Link to="/travel-insurance" className="back-link">← Travel Insurance</Link>

      <section className="cov-hero">
        <h1 className="cov-title">{coverage.title}</h1>
        <p className="cov-subtitle">{coverage.subtitle}</p>
      </section>

      <div className="cov-highlights">
        {coverage.highlights.map((h, i) => (
          <div key={i} className="cov-highlight">
            <span className="cov-highlight-value">{h.value}</span>
            <span className="cov-highlight-label">{h.label}</span>
          </div>
        ))}
      </div>

      {coverage.sections.map((section, i) => (
        <section key={i} className="cov-section">
          <h3 className="cov-section-title">{section.heading}</h3>
          <div className="cov-list">
            {section.items.map((item, j) => (
              <div key={j} className="cov-list-item">
                <span className="cov-check">✓</span>
                <span className="cov-text">{item}</span>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="cov-section">
        <h3 className="cov-section-title">Key Conditions</h3>
        <div className="cov-list">
          {coverage.conditions.map((c, i) => (
            <div key={i} className="cov-list-item">
              <span className="cov-check">✓</span>
              <span className="cov-text">{c}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="cov-section">
        <h3 className="cov-section-title">Exclusions</h3>
        <div className="cov-list">
          {coverage.exclusions.map((e, i) => (
            <div key={i} className="cov-list-item">
              <span className="cov-x">✕</span>
              <span className="cov-text">{e}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="cov-limit-banner">
        <span className="cov-limit-label">Coverage Limit</span>
        <span className="cov-limit-value">{coverage.limit}</span>
      </div>

      <div className="cov-close-wrap">
        <Link to="/travel-insurance" className="cov-close">Close</Link>
      </div>

      <footer className="powered-by">Powered by Visa</footer>
    </div>
  );
}
